import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Api, PAYMENT_SERVICE } from '../../api/api';
import { ModalController } from '@ionic/angular';
import Payment from '../../models/Payment';
import { environment } from '../../../environments/environment';
import { PaymentMethod } from '../../enums/PaymentMethod';
import Venue from '../../models/Venue';
import Order from '../../models/Order';
import { PaymentStatus } from '../../models/PaymentStatus';
import Customer from '../../models/Customer';
import { MatSnackBar } from '@angular/material/snack-bar';
import { paymentFromOrders, sleep } from 'src/smoothr-web-app-core/utils/utils';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { AdyenCheckout, GooglePay } from '@adyen/adyen-web';
import { CustomCard } from '@adyen/adyen-web';
import { AppComponent } from 'src/app/app.component';
import { paymentBody } from 'src/smoothr-web-app-core/models/Converter';
import * as Sentry from '@sentry/angular';

export enum PaymentModalResult {
	SUCCESS,
	ERROR,
	DISMISS,
}

// tslint:disable-next-line:prefer-const
declare var wpwlOptions: any;
// @ts-ignore
const applePayAvailable = !!window.ApplePaySession;

@Component({
	selector: 'app-payment-modal',
	templateUrl: './payment-modal.component.html',
	styleUrls: ['./payment-modal.component.scss'],
})
export class PaymentModalComponent implements OnInit {
	payment: Payment;
	venue: Venue;
	orders: Order[];
	customer: Customer;
	loading = false;
	paymentMethods: PaymentMethod[] = [];
	selectedPaymentMethod: PaymentMethod = null;
	openTime: number;
	paymentAttemptCount = 0;
	previousPaymentMethod: PaymentMethod | null = null;
	pm = PaymentMethod;
	vrUrl = environment.baseUrl + PAYMENT_SERVICE + 'vr/pay/';
	environment = environment;
	payments = {};
	wpwlOptionsBase = {
		locale: 'de',
		style: 'plain',
	};
	loadingWordline = false;
	forceAddressInput: false;
	eServiceSaveCard = false;
	isAdyenLoading = false;
	onPayment: (Payment) => void;
	@ViewChild('iframeContainer', { static: false }) iframeContainer: ElementRef;
	constructor(
		// private repository: RepositoryService,
		private modalCtrl: ModalController,
		private analytics: AnalyticsService,
		private snackBarCtrl: MatSnackBar,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {}

	get order(): Order {
		return this.orders?.[0] ?? null;
	}

	static async show(
		modalCtrl: ModalController,
		venue: Venue,
		orders: Order[],
		customer: Customer,
		tip: number = 0,
		forceAddressInput: boolean = false,
		onPayment: (Payment) => void
	): Promise<{ result: PaymentModalResult; error: any; payment: Payment }> {
		const paymentMethods: PaymentMethod[] = [];
		if (venue.ccEnabled) {
			paymentMethods.push(PaymentMethod.CREDIT_CARD);
		}
		if (venue.gpEnabled) {
			paymentMethods.push(PaymentMethod.GPAY);
		}
		if (venue.apEnabled && applePayAvailable) {
			paymentMethods.push(PaymentMethod.APAY);
		}
		let addPayPal = true;
		// try {
		// 	const isFoodSpot = orders[0]?.preorder?.type == PreorderType.FOOD_SPOT;
		// 	if (isFoodSpot) {
		// 		addPayPal = false;
		// 	}
		// } catch (e) {

		// }
		if (venue.ppEnabled && addPayPal) {
			paymentMethods.push(PaymentMethod.PAYPAL);
		}
		if (venue.sbEnabled) {
			paymentMethods.push(PaymentMethod.SOFORTBANKING);
		}

		//TURN OF FOR TABILO
		if (venue.cashEnabled && venue.cashPreorderEnabled && environment.enableCashPayment) {
			paymentMethods.push(PaymentMethod.CASH);
		}
		if (venue.ccvEnabled) {
			paymentMethods.push(PaymentMethod.CCV);
		}
		if (venue.adyenEnabled) {
			paymentMethods.push(PaymentMethod.ADYEN);
		}
		if (venue.worldlineEnabled) {
			paymentMethods.push(PaymentMethod.WORLDLINE);
		}
		const modal = await modalCtrl.create({
			component: PaymentModalComponent,
			componentProps: {
				payment: paymentFromOrders(orders, tip),
				paymentMethods,
				venue,
				orders,
				customer,
				forceAddressInput,
				onPayment,
				openTime: Date.now(),
			},
			backdropDismiss: false,
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data as any;
	}

	ngOnInit() {
		this.analytics.screenLoadTime('payment', Date.now() - (this.openTime ?? Date.now()));
		this.analytics.paymentModalOpened(this.order);
	}
	async tryToPayWithGooglePay(googleToken: string) {
		try {
			this.payment = {
				...this.payment,
				method: PaymentMethod.GPAY,
				adyenPayment: {
					gpToken: googleToken,
				},
			};
			const response = await Api.createPayment(this.payment);
			this.onPayment(response);
			if (response.status == PaymentStatus.created) {
				await sleep(2500);
				const result = await Api.getPayment(response._id);
				this.onPayment(result);
				if (result.data.status === PaymentStatus.done) {
					await this.modalCtrl.dismiss({
						result: PaymentModalResult.SUCCESS,
						payment: result,
					});
				}
			}
		} catch (e) {}
	}
	async loadGpayAdyen() {
		try {
			await sleep(500);

			const handlePayment = (state: any, component: any) => {
				if (state?.isValid && state?.data?.paymentMethod?.googlePayToken) {
					this.tryToPayWithGooglePay(state?.data?.paymentMethod?.googlePayToken);
				}
			};

			const handleAdditionalDetails = (state: any, component: any) => {
				// Handle additional details for 3DS, etc.
			};

			const configObj = {
				clientKey: 'test_AVFLPT5W6FEVJLMHFUQPOVIUNAJB7WZR',
				environment: 'test', // Change to 'live' for production
				countryCode: 'DE',
				currency: 'EUR',
				onSubmit: (state, component) => {
					handlePayment(state, component);
				},
				onAdditionalDetails: (state, component) => {
					handleAdditionalDetails(state, component);
				},
				locale: 'de_DE',
				analytics: {
					enabled: false, // Disable analytics to avoid CORS errors
				},
				paymentMethodsConfiguration: {
					googlepay: {
						environment: 'TEST' as any, // Change to 'PRODUCTION' for live
						amount: { currency: 'EUR', value: 1000 }, // Specify the amount
						configuration: {
							gatewayMerchantId: 'DeanAndDavid_EComtest_TEST',
							merchantId: 'DeanAndDavid_EComtest_TEST',
							merchantName: 'Your Merchant Name',
						},
						buttonColor: 'black', // Optional: Customize the button color
						buttonType: 'buy', // Optional: Customize the button type
						onPaymentDataChanged: paymentData => {
							// Optional: Handle payment data changes
						},
						onPaymentAuthorized: paymentData => {
							if (paymentData.paymentMethodData && paymentData.paymentMethodData.tokenizationData) {
								const gpayToken = paymentData.paymentMethodData.tokenizationData.token;
								this.tryToPayWithGooglePay(gpayToken);
							}
							return { transactionState: 'SUCCESS' }; // or 'ERROR' depending on your logic
						},
					},
				},
			} as any;

			const checkout = await AdyenCheckout(configObj);
			new GooglePay(checkout, {
				buttonType: 'checkout',
				buttonColor: 'white',

				// The 'merchant configuration' can be set, but in the majority of cases you don't have to, because the values are returned by your backend when retrieving the configured payment methods
				configuration: {
					gatewayMerchantId: 'DeanAndDavid_EComtest_TEST',
					merchantId: 'DeanAndDavid_EComtest_TEST',
					merchantName: 'Your Merchant Name',
				},
			}).mount('#gpay-container');
		} catch (e) {
		}
	}

	async paymentMethodAdyen() {
		await sleep(1000);

		const configuration = {
			locale: 'de_DE',
			environment: 'test',
			clientKey: 'test_AVFLPT5W6FEVJLMHFUQPOVIUNAJB7WZR',
			countryCode: 'DE',
			currency: 'EUR',
			analytics: {
				enabled: false, //disable analytics cors errors
			},
			hasHolderName: true,
			holderNameRequired: true,
			billingAddressRequired: true,
		} as any;

		const checkout = await AdyenCheckout(configuration);

		const customCard = new CustomCard(checkout, {
			type: 'card',
			brands: ['mc', 'visa', 'amex', 'bcmc', 'maestro'],
			styles: {
				error: { color: 'red' },
				validated: { color: 'green' },
				placeholder: { color: '#d8d8d8' },
			},

			onLoad: function (value) {
			},
			onError: function (err) {
			},
		}).mount('#customCard-container');

		document.getElementById('payButton').addEventListener('click', () => {
			//Manuel submit form
			if (customCard.state.data && customCard.state.isValid) {
				const stateData = customCard.state.data;
				const object = {
					card: {
						number: stateData.encryptedCardNumber,
						month: stateData.encryptedExpiryMonth,
						year: stateData.encryptedExpiryYear,
						code: stateData.encryptedSecurityCode,
					},
				};
				this.startPaymentAdyen(object);
			}
			// customCard.submit();
		});
	}
	async startPaymentAdyen(data: {
		card: {
			number: any;
			month: any;
			year: any;
			code: any;
		};
	}) {
		try {
			this.isAdyenLoading = true;
			this.payment = {
				...this.payment,
				method: PaymentMethod.CREDIT_CARD,
				adyenPayment: data,
			};
			this.payment = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			if (this.payment.status === PaymentStatus.created) {
				await sleep(4000);
				const paymentData = (await Api.getPayment(this.payment._id)).data;
				this.onPayment(paymentData);
				if (this.payment?.adyenPayment?.redirectUrl) {
					if (AppComponent.isNativeApp) {
						this.loadIframe(this.payment);

						return;
					}
					const form = document.createElement('form');
					form.method = 'POST';
					form.action = this.payment.adyenPayment.redirectUrl;

					const paReqInput = document.createElement('input');
					paReqInput.type = 'hidden';
					paReqInput.name = 'PaReq';
					paReqInput.value = this.payment.adyenPayment.cardAuthRequestPaReq;
					form.appendChild(paReqInput);

					const mdInput = document.createElement('input');
					mdInput.type = 'hidden';
					mdInput.name = 'MD';
					mdInput.value = this.payment.adyenPayment.cardPaymentSessionMd;
					form.appendChild(mdInput);

					document.body.appendChild(form);
					form.submit();
				}
			}
		} catch (e) {
			this.isAdyenLoading = false;
		}
	}
	loadIframe(payment: Payment) {
		const htmlContent = `
		  <!DOCTYPE html>
		  <html>
		  <head>
			<title>Payment Redirect</title>
		  </head>
		  <body>
			<script>
			let lastUrl = location.href;

			  function submitForm(payment, formParams) {
				const form = document.createElement('form');
				form.method = 'POST';
				form.action = '${payment.adyenPayment.redirectUrl}';
				const paReqInput = document.createElement('input');
				paReqInput.type = 'hidden';
				paReqInput.name = 'PaReq';
				paReqInput.value = '${payment.adyenPayment.cardAuthRequestPaReq}';
				form.appendChild(paReqInput);
				const mdInput = document.createElement('input');
				mdInput.type = 'hidden';
				mdInput.name = 'MD';
				mdInput.value = '${payment.adyenPayment.cardPaymentSessionMd}';
				form.appendChild(mdInput);
				document.body.appendChild(form);
				form.submit();
			  }
			  window.addEventListener('message', function(event) {
				if (event.data.type === 'setData') {
				  const payment = event.data.payment;
				  const formParams = event.data.formParams;
				  submitForm(payment, formParams);
				}
			  });
		  
			  // Start polling for URL changes
			</script>
		  </body>
		  </html>
		`;

		// Create a Blob URL for the HTML content
		const blob = new Blob([htmlContent], { type: 'text/html' });
		const url = URL.createObjectURL(blob);

		// Create and append the iframe
		const iframe = document.createElement('iframe');
		iframe.src = url;
		iframe.style.width = '100%';
		iframe.style.height = '75vh'; // Adjust height as needed
		iframe.id = 'custom_3d';

		this.iframeContainer.nativeElement.appendChild(iframe);

		// Clean up the Blob URL after use
		iframe.onload = param => {
			iframe.contentWindow.postMessage(
				{
					type: 'setData',
					payment: this.payment,
					formParams: this.payment,
				},
				'*'
			);
			this.checkPayment(this.payment);
			window.addEventListener('message', this.handleMessage.bind(this));

			URL.revokeObjectURL(url);
		};
	}

	handleMessage(event: MessageEvent) {}
	async checkPayment(payment: Payment, attempt: number = 0) {
		setTimeout(async () => {
			try {
				const result = (await Api.getPayment(payment._id)).data;
				this.onPayment(result);
				if (result.status != PaymentStatus.created) {
					this.snackBarCtrl.open('Payment was successfully done :)', null, {
						duration: 2500,
					});
					await this.modalCtrl.dismiss({
						result: PaymentModalResult.SUCCESS,
						payment: result,
					});
				}
			} catch (e) {}
		}, 1000);
	}
	async loadAdyenApay() {
		const initiateApplePay = () => {
			const paymentRequest = {
				countryCode: 'DE',
				currencyCode: 'EUR',
				environment: 'test',
				total: {
					label: 'Your Merchant Name',
					amount: '0.50',
				},
				clientKey: 'test_AVFLPT5W6FEVJLMHFUQPOVIUNAJB7WZR',
				supportedNetworks: ['visa', 'masterCard', 'amex'],
				merchantCapabilities: ['supports3DS'],
			};
			//@ts-ignore
			const session = new ApplePaySession(3, paymentRequest);

			session.onvalidatemerchant = event => {
				// Call your server to validate the merchant

				fetch('https://dev-api.smoothr.de/v3/payment/adyen/sessions/venues/66605316ad87cfffc93b3e12', {
					method: 'GET',
					headers: {
						'Content-Type': 'application/json',
					},
				})
					.then(response => response.json())
					.then(merchantSession => {
						session.completeMerchantValidation(merchantSession);
					})
					.catch(error => console.error('Merchant validation failed:', error));
			};

			session.onpaymentauthorized = event => {
				const paymentData = event.payment;
				// Process the payment via your backend (forward to Adyen)
				// fetch('/process-payment', {
				//   method: 'POST',
				//   body: JSON.stringify({ paymentData }),
				//   headers: {
				//     'Content-Type': 'application/json'
				//   }
				// })
				//     .then(response => response.json())
				//     .then(data => {
				//       session.completePayment(ApplePaySession.STATUS_SUCCESS);
				//     })
				//     .catch(error => {
				//       session.completePayment(ApplePaySession.STATUS_FAILURE);
				//       console.error('Payment processing failed:', error);
				//     });
			};

			session.begin();
		};
		initiateApplePay();

		// try {
		// 	await sleep(500);

		// 	const handlePayment = (state: any, component: any) => {
		// 		console.log(state, component, state?.isValid);
		// 	};

		// 	const handleAdditionalDetails = (state: any, component: any) => {
		// 		console.log(state, component);
		// 		// Handle additional details for 3DS, etc.
		// 	};

		// 	const configObj = {
		// 		clientKey: 'test_AVFLPT5W6FEVJLMHFUQPOVIUNAJB7WZR',
		// 		environment: 'test', // Change to 'live' for production
		// 		countryCode: 'DE',
		// 		currency: 'EUR',
		// 		onSubmit: (state, component) => {
		// 			handlePayment(state, component);
		// 		},
		// 		onAdditionalDetails: (state, component) => {
		// 			handleAdditionalDetails(state, component);
		// 		},
		// 		locale: 'de_DE',
		// 		analytics: {
		// 			enabled: false, // Disable analytics to avoid CORS errors
		// 		},
		// 		paymentMethodsConfiguration: {
		// 			applepay: {
		// 				environment: 'TEST' as any, // Change to 'PRODUCTION' for live
		// 				amount: { currency: 'EUR', value: 1000 }, // Specify the amount
		// 				configuration: {
		// 					gatewayMerchantId: 'DeanAndDavid_EComtest_TEST',
		// 					merchantId: 'DeanAndDavid_EComtest_TEST',
		// 					merchantName: 'Your Merchant Name',
		// 				},
		// 				buttonColor: 'black', // Optional: Customize the button color
		// 				buttonType: 'buy', // Optional: Customize the button type
		// 				onPaymentDataChanged: paymentData => {
		// 					console.log('Payment data changed:', paymentData);
		// 					// Optional: Handle payment data changes
		// 				},
		// 				onPaymentAuthorized: paymentData => {
		// 					console.log('Payment authorized:', paymentData);
		// 					if (paymentData.paymentMethodData && paymentData.paymentMethodData.tokenizationData) {
		// 						const gpayToken = paymentData.paymentMethodData.tokenizationData.token;
		// 						this.tryToPayWithGooglePay(gpayToken);
		// 					}
		// 					return { transactionState: 'SUCCESS' }; // or 'ERROR' depending on your logic
		// 				},
		// 			},
		// 		},
		// 	} as any;

		// 	const checkout = await AdyenCheckout(configObj);
		// 	new ApplePaySession()
		// 	new ApplePay(checkout, {
		// 		buttonColor: 'white',
		// 		amount: { currency: 'EUR', value: 1000 },
		// 		// The 'merchant configuration' can be set, but in the majority of cases you don't have to, because the values are returned by your backend when retrieving the configured payment methods
		// 		configuration: {
		// 			merchantId: 'DeanAndDavid_EComtest_TEST',
		// 			merchantName: 'Your Merchant Name',
		// 		},
		// 	}).mount('#applepay-container');
		// } catch (e) {
		// 	console.log('Error loading Google Pay:', e);
		// }
	}
	async loadPayment() {
		if (this.loading) {
			return;
		}
		this.loading = true;
		this.resetJs();
		this.payment._id = null;
		this.payment.method = this.selectedPaymentMethod;
		// reset payment container
		this.selectedPaymentMethod = null;
		wpwlOptions = null;
		this.cdr.detectChanges();
		this.selectedPaymentMethod = this.payment.method;
		this.paymentAttemptCount++;
		this.analytics.paymentMethodSelected(this.selectedPaymentMethod, this.order, this.paymentAttemptCount, this.previousPaymentMethod);
		this.previousPaymentMethod = this.selectedPaymentMethod;

		try {
			switch (this.selectedPaymentMethod) {
				case PaymentMethod.PAYPAL:
					await this.loadPaypal();
					break;
				case PaymentMethod.CREDIT_CARD:
					if (this.venue.eServiceEnabled) {
						await this.loadEServiceCreditCard();
					} else {
						await this.loadCreditCard();
					}
					break;
				case PaymentMethod.SOFORTBANKING:
					await this.loadKlarna();
					break;
				case PaymentMethod.GPAY:
					if (this.venue.eServiceEnabled) {
						await this.loadEServiceGooglePay();
					} else if (this.venue.adyenEnabled) {
						await this.loadGpayAdyen();
					} else {
						await this.loadGpay();
					}
					break;
				case PaymentMethod.APAY:
					if (this.venue.eServiceEnabled) {
						await this.loadEServiceApplePay();
					} else if (this.venue.adyenEnabled) {
						await this.loadAdyenApay();
					} else {
						await this.loadApay();
					}
					break;
				case PaymentMethod.CASH:
					await this.loadCashButton();
					break;
				case PaymentMethod.CCV:
					await this.ccvPay(PaymentMethod.CCV);
					break;
				case PaymentMethod.ADYEN:
					await this.paymentMethodAdyen();
					break;
			}
			Sentry.captureMessage(`Payment with Type ${this.selectedPaymentMethod} loaded successfully`);
		} catch (e) {
			Sentry.captureException(e);
			console.error(e);
			this.snackBarCtrl.open(this.translate.instant('payment_modal.error'));
			const { error_type, ...extraParams } = this.classifyPaymentLoadError(e);
			this.orders.forEach(order => {
				this.analytics.paymentLoadingFailed(order, this.selectedPaymentMethod, error_type, extraParams);
			});
			this.selectedPaymentMethod = null;
			this.loading = false;
			return false;
		}
		this.loading = false;
		this.orders.forEach(order => {
			this.analytics.paymentLoaded(order, this.selectedPaymentMethod);
		});
	}

	async ccvPay(paymentMethod: PaymentMethod) {
		try {
			this.payment = await Api.createCCVPayment(this.venue._id, this.payment);
			this.onPayment(this.payment);
			this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

			this.loading = false;
			// await this.navigateToPaymentPape(this.payment.walleePayment.url);
		} catch (e) {
			console.error(e, 'error while ccv button setup');
			this.loading = false;
			throw e;
		}
	}
	async loadVr(paymentMethod: PaymentMethod, paramsBuilder: (payment: Payment) => any) {
		try {
			const prevPayment = this.payments[paymentMethod];
			if (prevPayment) {
				this.payment = prevPayment;
				this.onPayment(this.payment);
				wpwlOptions = JSON.parse(JSON.stringify(this.payment.wpwlOptions));
				await this.loadVrJs(this.payment.vrPayment.checkoutId);
				return;
			}
			this.payment = await Api.createPayment(this.payment);
			this.onPayment(this.payment);
			wpwlOptions = {
				...this.wpwlOptionsBase,
				...paramsBuilder(this.payment),
			};
			this.payment.wpwlOptions = JSON.parse(JSON.stringify(wpwlOptions));
			this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

			await this.loadVrJs(this.payment.vrPayment.checkoutId);
		} catch (e) {
			throw e;
		}
	}

	async loadEServiceGooglePay() {
		const prevPayment = this.payments[PaymentMethod.GPAY];
		if (prevPayment) {
			this.payment = prevPayment;
			return;
		}
		if (!this.payment.eServicePayment) {
			this.payment.eServicePayment = {};
		}
		this.payment = await Api.createPayment(this.payment);
		this.payments[PaymentMethod.GPAY] = JSON.parse(JSON.stringify(this.payment));
	}

	async loadEServiceApplePay() {
		const prevPayment = this.payments[PaymentMethod.APAY];
		if (prevPayment) {
			this.payment = prevPayment;
			return;
		}
		if (!this.payment.eServicePayment) {
			this.payment.eServicePayment = {};
		}
		this.payment = await Api.createPayment(this.payment);
		this.payments[PaymentMethod.APAY] = JSON.parse(JSON.stringify(this.payment));
	}

	async loadEServiceCreditCard() {
		const prevPayment = this.payments[PaymentMethod.CREDIT_CARD];
		if (prevPayment) {
			this.payment = prevPayment;
			return;
		}
		if (!this.payment.eServicePayment) {
			this.payment.eServicePayment = {};
		}
		this.payment.eServicePayment.saveCard = this.eServiceSaveCard;
		this.payment = await Api.createPayment(this.payment);
		this.payments[PaymentMethod.CREDIT_CARD] = JSON.parse(JSON.stringify(this.payment));
	}

	async onEServiceSaveCardChange() {
		if (!this.payments[PaymentMethod.CREDIT_CARD]) {
			return;
		}
		this.payments[PaymentMethod.CREDIT_CARD] = null;
		this.payment._id = null;
		this.payment.eServicePayment = null;
		this.loading = true;
		this.cdr.detectChanges();
		await this.loadEServiceCreditCard();
		this.loading = false;
		this.cdr.detectChanges();
	}

	async onEServiceSuccess(result: Payment) {
		this.payment.status = PaymentStatus.done;
		const redirectUrl = (result as any)?.redirectUrl
			|| (result as any)?.redirect_url
			|| (result as any)?.successUrl
			|| (result as any)?.url;
		await this.modalCtrl.dismiss({
			result: PaymentModalResult.SUCCESS,
			payment: { ...this.payment, ...result },
		});
		if (redirectUrl) {
			window.location.href = redirectUrl;
		}
	}

	onEServiceError(message: string) {
		this.snackBarCtrl.open(message || this.translate.instant('payment_modal.error'));
		this.selectedPaymentMethod = null;
		this.loading = false;
	}

	async loadCreditCard() {
		if (!this.venue.vrPaymentEnabled && this.venue.ppFullIntegrationEnabled) {
			await this.loadPaypalCreditCard();
		}
		await this.loadVr(PaymentMethod.CREDIT_CARD, payment => ({
			billingAddress: this.getBillingAddress(this.order, this.customer),
			total: payment.sum,
			mandatoryBillingFields: {
				country: true,
				state: false,
				city: true,
				postcode: true,
				street1: true,
			},
		}));
	}

	async loadGpay() {
		await this.loadVr(PaymentMethod.GPAY, payment => ({
			googlePay: {
				gatewayMerchantId: payment.merchantId,
				allowedAuthMethods: ['PAN_ONLY'],
				allowedCardNetworks: ['MASTERCARD', 'VISA'],
				merchantName: this.venue.name,
				merchantId: environment.GPAY.MERCHANT_ID,
				gateway: environment.GPAY.GATEWAY,
				assuranceDetailsRequired: true,
			},
		}));
	}

	async loadApay() {
		await this.loadVr(PaymentMethod.APAY, payment => ({
			applePay: {
				version: 3,
				displayName: this.venue.name,
				total: this.payment.sum,
				currencyCode: this.payment.currency,
				checkAvailability: 'canMakePayments',
				merchantIdentifier: environment.APAY_MERCHANT_ID,
				style: 'black',
				merchantCapabilities: ['supports3DS'],
				supportedNetworks: ['masterCard', 'visa'],
				shippingType: 'storePickup',
				createCheckout: () => {
					return this.payment.vrPayment.checkoutId;
				},
				onCancel: () => {
					this.loading = false;
				},
				onPaymentAuthorized: applePayment => {
					// this.payment.status = PaymentStatus.done;
					// this.orders.forEach(order => {
					// 	this.analytics.purchase(order, this.payment);
					// });
					// this.modalCtrl.dismiss({
					// 	result: PaymentModalResult.SUCCESS,
					// 	payment: this.payment,
					// });
				},
			},
		}));
	}

	getBillingAddress(order: Order, customer: Customer): { country: string; city: string; postcode: string; street1: string } {
		if (customer && customer.street) {
			return {
				country: customer.country.toUpperCase(),
				city: customer.city,
				postcode: customer.postalCode,
				street1: customer.street + ' ' + customer.number,
			};
		}
		if (this.forceAddressInput) {
			return {
				country: '',
				city: '',
				postcode: '',
				street1: '',
			};
		}
		return {
			country: order.preorder?.country?.toUpperCase(),
			city: order.preorder?.city,
			postcode: order.preorder?.postalCode,
			street1: order.preorder?.street + ' ' + order.preorder?.number,
		};
	}

	resetJs() {
		const head = document.getElementsByTagName('head')[0];
		const prevVrJs = document.getElementById('vr-js');
		if (prevVrJs) {
			head.removeChild(prevVrJs);
		}
		const prevPpJs = document.getElementById('paypal-js');
		if (prevPpJs) {
			head.removeChild(prevPpJs);
		}
	}

	loadVrJs(checkoutId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			let widget = null;
			while (!widget) {
				this.cdr.detectChanges();
				await sleep(10);
				widget = document.getElementsByClassName('paymentWidgets')[0];
			}
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.id = 'vr-js';
			script.src = `${environment.VR_URL}?checkoutId=${checkoutId}`;
			head.appendChild(script);
			script.onerror = () => {
				reject(this.createAnalyticsError('Payment widget script failed to load', 'SDK_LOAD_FAILED'));
				this.loading = false;
			};
			script.onload = async () => {
				this.loading = false;

				resolve();
			};
		});
	}

	loadPaypal(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.PAYPAL, 'paypal-button');
	}

	loadKlarna(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.SOFORTBANKING, 'klarna-button');
	}

	loadPaypalCreditCard(): Promise<any> {
		return this.loadPaypalBase(PaymentMethod.CREDIT_CARD, 'paypal-cc-button');
	}

	loadPaypalJs(payment: Payment): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			if (!payment.merchantId) {
				reject(this.createAnalyticsError('PayPal not configured for this venue', 'PAYPAL_NOT_CONFIGURED'));
				return;
			}
			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.id = 'paypal-js';
			script.src = environment.PAYPAL.JS_URL + payment.currency + '&merchant-id=' + payment.merchantId;
			// script.src =
			// 	'https://www.paypal.com/sdk/js?client-id=Ac5vAqN4prOBX3YUFPX_L9Bmz3ncsdmOgkz7LuE2zP3yDbIaJB0y3SD4CTBdJqoHQhcWP6-WMklA-thJ&integration-date=2020-09-04&components=buttons,funding-eligibility&currency=';
			script.setAttribute('data-partner-attribution-id', environment.PAYPAL.BN_CODE);
			head.appendChild(script);
			script.onerror = () => reject(this.createAnalyticsError('PayPal SDK failed to load', 'SDK_LOAD_FAILED'));
			script.onload = () => resolve();
		});
	}

	loadPaypalButton(payment: Payment, fundingSource: any, elementId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			// @ts-ignore
			const button = paypal.Buttons({
				intent: 'capture',
				commit: true,
				vault: false,
				enableStandardCardFields: true,
				createOrder: (data, actions) => {
					return payment.paypal.orderId;
				},
				onCancel: close => {
					this.orders.forEach(order => {
						this.analytics.paymentFailed(order, payment, PaymentMethod.PAYPAL, 'paypal_cancelled', {
							error_code: 'USER_CANCELLED',
							error_message: 'User cancelled payment in PayPal',
						});
					});
					this.modalCtrl.dismiss({
						result: PaymentModalResult.ERROR,
						error: {
							error: PaymentMethod.PAYPAL,
							orderId: this.order?._id,
						},
					});
					return;
				},

				onApprove: data => {
					this.finishPaypal(payment, data)
						.then(() => {
							resolve();
						})
						.catch(err => {
							reject(err);
						});
				},

				onError: err => {
					this.loading = true;
					const message = err?.message || (typeof err === 'string' ? err : undefined);
					const code = err?.code;
					this.orders.forEach(order => {
						this.analytics.paymentFailed(order, payment, PaymentMethod.PAYPAL, 'paypal_error', {
							...(code != null ? { error_code: code } : {}),
							...(message ? { error_message: message } : {}),
						});
					});
					this.modalCtrl.dismiss({
						result: PaymentModalResult.ERROR,
						error: err,
					});
				},
				fundingSource,
			});
			if (!button.isEligible()) {
				reject(this.createAnalyticsError('PayPal button not eligible', 'PAYPAL_BUTTON_INELIGIBLE'));
				return;
			}
			try {
				await button.render('#' + elementId);
				resolve();
			} catch (error) {
				reject(error);
			}
		});
	}

	async finishPaypal(payment: Payment, data: any) {
		this.loading = true;
		try {
			const result = await Api.finishPaypalPayment(data.orderID);
			this.onPayment(result);
			this.orders.forEach(order => {
				this.analytics.purchase(order, result);
			});
			await this.modalCtrl.dismiss({
				result: PaymentModalResult.SUCCESS,
				payment: result,
			});
		} catch (e) {
			const { error_type, ...extraParams } = this.classifyPaymentError(e);
			this.orders.forEach(order => {
				this.analytics.paymentFailed(order, payment, PaymentMethod.PAYPAL, error_type, extraParams);
			});
			this.loading = false;
			throw e;
		}
	}

	private classifyPaymentLoadError(
		error: any
	): { error_type: string; http_status?: number; error_code?: number | string; error_message?: string } {
		const status: number | undefined = error?.status ?? error?.response?.status;
		const code: number | string | undefined = error?.error?.code ?? error?.response?.data?.code ?? error?.analyticsCode ?? error?.code;
		const message: string | undefined = error?.error?.message ?? error?.response?.data?.message ?? error?.message;

		return {
			error_type: 'payment_load_failed',
			...(status ? { http_status: status } : {}),
			...(code != null ? { error_code: code } : {}),
			...(message ? { error_message: message } : {}),
		};
	}

	private classifyPaymentError(
		error: any
	): { error_type: string; http_status?: number; error_code?: number | string; error_message?: string } {
		if (!error) {
			return { error_type: 'unknown' };
		}

		const isTimeout = error?.code === 'ECONNABORTED' || (error?.message && error.message.includes('timeout'));
		const status: number | undefined = error?.status ?? error?.response?.status;
		const code: number | string | undefined = error?.error?.code ?? error?.response?.data?.code ?? error?.analyticsCode ?? error?.code;
		const message: string | undefined = error?.error?.message ?? error?.response?.data?.message ?? error?.message;

		if (isTimeout) {
			return {
				error_type: 'network_error',
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (!status) {
			return {
				error_type: 'network_error',
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (status >= 500) {
			return {
				error_type: 'server_error',
				http_status: status,
				...(code != null ? { error_code: code } : {}),
				...(message ? { error_message: message } : {}),
			};
		}

		if (status === 400) {
			if (code != null && [2512, 2516].includes(Number(code))) {
				return {
					error_type: 'payment_declined',
					http_status: status,
					error_code: code,
					...(message ? { error_message: message } : {}),
				};
			}

			if (code != null && [2003, 2004, 2023].includes(Number(code))) {
				return {
					error_type: 'order_creation_failed',
					http_status: status,
					error_code: code,
					...(message ? { error_message: message } : {}),
				};
			}
		}

		return {
			error_type: 'unknown',
			...(status ? { http_status: status } : {}),
			...(code != null ? { error_code: code } : {}),
			...(message ? { error_message: message } : {}),
		};
	}

	private createAnalyticsError(message: string, analyticsCode: string): Error & { analyticsCode: string } {
		const error = new Error(message) as Error & { analyticsCode: string };
		error.analyticsCode = analyticsCode;
		return error;
	}

	loadPaypalBase(paymentMethod: PaymentMethod, elementId: string): Promise<void> {
		return new Promise<void>(async (resolve, reject) => {
			try {
				const prevPayment = this.payments[paymentMethod];
				if (prevPayment) {
					this.payment = prevPayment;
					this.onPayment(this.payment);
					await this.loadPaypalJs(this.payment);
					// @ts-ignore
					await this.loadPaypalButton(this.payment, this.paymentMethodToPaypalFunding(paymentMethod), elementId);
					resolve();
					return;
				}
				this.payment = await Api.createPayment(this.payment);
				this.onPayment(this.payment);
				this.payments[paymentMethod] = JSON.parse(JSON.stringify(this.payment));

				await this.loadPaypalJs(this.payment);
				// @ts-ignore
				await this.loadPaypalButton(this.payment, this.paymentMethodToPaypalFunding(paymentMethod), elementId);
				resolve();
			} catch (e) {
				console.error('error while paypal setup', e);
				reject(e);
			}
		});
	}

	paymentMethodToPaypalFunding(paymentMethod: PaymentMethod): any {
		switch (paymentMethod) {
			case PaymentMethod.CREDIT_CARD:
				// @ts-ignore
				return paypal.FUNDING.CARD;
			case PaymentMethod.SOFORTBANKING:
				// @ts-ignore
				return paypal.FUNDING.CARD;
			case PaymentMethod.PAYPAL:
				// @ts-ignore
				return paypal.FUNDING.PAYPAL;
			default:
				return null;
		}
	}

	async dismiss() {
		if (this.loading) {
			return;
		}
		if (this.orders[0]?.sanifairVouchers?.length > 0) {
			// try {
			// 	await Api.extendSanifairVoucher(this.orders[0]?._id);
			// } catch (e) {
			// 	this.snackBarCtrl.open('Smth go wrong while extend order...', null, {
			// 		duration: 4000
			// 	});
			// }
		}
		await this.modalCtrl.dismiss({
			result: PaymentModalResult.DISMISS,
		});
	}
	async loadCashButton() {
		this.selectedPaymentMethod = PaymentMethod.CASH;
	}
	async loadCashPay() {
		this.loading = true;
		this.payment = await Api.createPayment(this.payment);
		this.onPayment(this.payment);
		this.loading = false;
		await this.modalCtrl.dismiss({
			result: PaymentModalResult.SUCCESS,
			payment: this.payment,
		});
	}
	navigateCvv(url: string) {
		window.location.replace(url);
	}

	async loadWordline() {
		try {
			this.loadingWordline = true;
			this.payment._id = undefined;
			const payment = await Api.createWorldLinePayment(paymentBody(this.payment), this.venue._id);
			this.onPayment(payment);
			this.payment = payment;
			// this.repository.paymentId$.next(payment?._id);
			if (payment?.worldlinePayment?.paymentPage) {
				try {
					this.orders.forEach(order => {
						this.analytics.paymentLoaded(order, PaymentMethod.WORLDLINE);
					});
				} catch (trackingError) {
					console.error('Worldline success tracking failed', trackingError);
				}
				window.location.assign(payment?.worldlinePayment?.paymentPage);
			} else {
				throw new Error('Problems with creating payment...');
			}
			setTimeout(() => {
				//waiting when page will be open
				this.loadingWordline = false;
			}, 1200);
		} catch (e) {
			this.loadingWordline = false;
			try {
				Sentry.captureException(e instanceof Error ? e : new Error(String(e)), {
					tags: {
						payment_method: PaymentMethod.WORLDLINE,
						payment_stage: 'create_worldline_payment_page',
					},
					contexts: {
						worldline: {
							venue_id: this.venue?._id,
							venue_name: this.venue?.name,
							payment_id: this.payment?._id,
							order_ids: this.orders.map(order => order?._id).filter(Boolean),
							order_codes: this.orders.map(order => order?.code).filter(Boolean),
							preorder_types: this.orders.map(order => order?.preorder?.type).filter(Boolean),
							order_types: this.orders.map(order => order?.type).filter(Boolean),
						},
					},
				});
				this.orders.forEach(order => {
					this.analytics.paymentLoadingFailed(order, PaymentMethod.WORLDLINE, 'worldline_payment_page_failed');
				});
			} catch (trackingError) {
				console.error('Worldline error tracking failed', trackingError);
			}

			this.snackBarCtrl.open('Error while creating payment.', null, {
				duration: 4000,
			});
		}
	}
}
